import { useState } from 'react';
import { Button, Menu, MenuItem, CircularProgress, Box, Snackbar, Alert } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuthStore, useUser } from '../../store/useAuthStore';

export function LabSwitcher() {
  const user = useUser();
  const { isSwitchingLab, switchLab } = useAuthStore();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [errorOpen, setErrorOpen] = useState(false);

  if (!user || user.labs.length <= 1) return null;

  const currentLab = user.labs.find((l) => l.id === user.defaultLabId);
  const otherLabs = user.labs.filter((l) => l.id !== user.defaultLabId);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!isSwitchingLab) setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = async (labId: string) => {
    handleClose();
    try {
      await switchLab(labId);
    } catch {
      setErrorOpen(true);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Button
        variant="outlined"
        color="inherit"
        endIcon={isSwitchingLab ? <CircularProgress size={14} color="inherit" /> : <KeyboardArrowDown />}
        onClick={handleOpen}
        disabled={isSwitchingLab}
        aria-label={`Current lab: ${currentLab?.name ?? 'none'}`}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        sx={{
          fontWeight: 600,
          bgcolor: (theme.customHeader as { buttonBgColor?: string })?.buttonBgColor ?? 'transparent',
          '&:hover': {
            bgcolor: (theme.customHeader as { buttonHoverBgColor?: string })?.buttonHoverBgColor,
          },
        }}
      >
        {isSwitchingLab ? 'Switching…' : (currentLab?.name ?? 'Select Lab')}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {otherLabs.map((lab) => (
          <MenuItem key={lab.id} onClick={() => handleSelect(lab.id)}>
            {lab.name}
          </MenuItem>
        ))}
      </Menu>
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setErrorOpen(false)} severity="error" sx={{ width: '100%' }}>
          Failed to switch lab. Please try again.
        </Alert>
      </Snackbar>
    </Box>
  );
}
